import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import coachnow_logo from "app/images/Logo_dark.svg";
import PriceCard from "./_components/PriceCard";
import BillingInformation from "./_components/BillingInformation";
import CheckoutLegalLinks from "app/components/CheckoutLegalLinks";
import price_tag from "app/images/green_price_tag.svg";
import onboarding from "app/images/pro-trial-golf-onboarding.png";
import { Toast } from "app/components/helpers";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import SuccessPage from "app/components/subscription/Upgrade/SuccessPage";
import { cls } from "app/utils";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Authenticated, Loading } from "app/components/Wrappers";
import PricingToggle from "./_components/PricingToggle";
import { useTranslation } from "react-i18next";
import { AuthService } from "app/services";
import { formatPrice, parseCurrency } from "app/utils/currency";
import { usePromoOffer } from "app/hooks/usePromoOffer";
import PromoResolveError from "app/components/PromoResolveError";
import { hasCanceledStorePlan, repriceTargets } from "app/utils/subscription";
import {
  mergePaywallContent,
  paywallBillingTerm,
  usePaywallFlag,
} from "app/hooks/usePaywallFlag";

type proPlanType = "coachpro+monthly" | "coachpro+yearly";

const PRO_PLAN_CODES: proPlanType[] = ["coachpro+yearly", "coachpro+monthly"];

// `purchase` renders the direct-buy variant (/purchase/pro): the checkout
// charges immediately instead of starting a trial, with matching copy.
const ProCheckout = ({ purchase = false }: { purchase?: boolean }) => {
  const Auth: any = AuthService;
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user);
  const [toastAlert, setToastAlert] = useState(null);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [repricedPlan, setRepricedPlan] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { plan } = useParams<{ plan?: string }>();
  const params = new URLSearchParams(location.search);
  const linkedPromo = params.get("promo") || undefined;
  // The code dropped for locking onto a term this page may not move to (see
  // below); a different code arriving on the same route is resolved as usual.
  const [droppedPromo, setDroppedPromo] = useState<string>();
  const promo = linkedPromo === droppedPromo ? undefined : linkedPromo;
  // A direct purchase by a user who already pays Stripe for a plan re-prices
  // that subscription in place (plan.upgradeTargets) instead of buying anew:
  // the other PRO term, or PRO over Analyze or Plus. The page then sells only the terms the
  // subscription may move to.
  const repriceTerms = purchase ? (repriceTargets(self, PRO_PLAN_CODES) as proPlanType[]) : [];
  const reprice = repriceTerms.length > 0;
  // A re-price is billed in the subscription's own currency, whatever the link
  // says: the API re-prices in it, so the price and the code are previewed in it.
  const currency = parseCurrency(reprice ? self.plan.currency : params.get("currency"));
  const hasPromo = Boolean(promo);
  const hasActiveTrial = self?.subscription?.trialEndsAt >= new Date();
  // A plan still renewing in a store keeps the page closed until it is
  // cancelled there; one already cancelled does not stand in the way of a
  // Stripe plan for the time it has left. A trial never follows a live plan.
  const canceledStorePlan = purchase && hasCanceledStorePlan(self);
  const sendHome =
    !reprice &&
    !canceledStorePlan &&
    (hasActiveTrial ||
      self?.subscription?.status === "active" ||
      self?.subscription?.status === "trial" ||
      self?.hasActiveSubscription ||
      self?.hasTrialSubscription);
  // The term the link names (/trial/coachpro+monthly), if any.
  const linkedPlan = PRO_PLAN_CODES.includes(plan as proPlanType)
    ? (plan as proPlanType)
    : undefined;
  // The trial paywall's copy can come from its GrowthBook flag, which is read
  // only once the paywall is on screen.
  const { flag: paywallFlag, ready: contentReady } = usePaywallFlag(
    "paywall-pro",
    {
      shown: Boolean(self) && !purchase && !sendHome,
      user: self,
      billingTermInUrl: Boolean(linkedPlan),
    },
  );
  // The term picked on the toggle; until then, the one the link names, else
  // the one the flag opens on.
  const [pickedPlan, setPlanCode] = useState<proPlanType>(linkedPlan);
  const planCode: proPlanType =
    pickedPlan ??
    (paywallBillingTerm(paywallFlag) === "monthly"
      ? "coachpro+monthly"
      : "coachpro+yearly");

  // Resolve the plan's price + any offer for the current (code, plan, currency),
  // waiting for auth. The hook owns the resolve dispatch + the freshness /
  // loading / error / retry logic (shared across the checkout routes).
  const { offer, baseAmount, loading, error: resolveError, lockedPlan, trialDays: promoTrialDays, resolveOffer } = usePromoOffer({
    code: promo,
    planCode,
    currency,
    ready: !!self,
  });
  // A term-locked code (tied to one billing term) resolves to that term's plan;
  // follow it so a locked link doesn't hang on the requested term's loader. A
  // lock onto a term this page may not move to (the plan the user is on) is
  // dropped with its code, as the API drops it: the page then prices the term
  // it can sell at the full price instead of waiting on a resolution the lock
  // never lets match.
  useEffect(() => {
    if (!lockedPlan) {
      return;
    }
    if (reprice && !repriceTerms.includes(lockedPlan as proPlanType)) {
      setDroppedPromo(linkedPromo);
    } else if (lockedPlan !== planCode) {
      setPlanCode(lockedPlan as proPlanType);
    }
    // planCode is a dep so a term toggle on a locked code re-asserts the lock
    // (snaps back) instead of leaving the page stuck on the mismatched term.
  }, [lockedPlan, planCode, reprice, repriceTerms]);
  useEffect(() => {
    if (reprice && !repriceTerms.includes(planCode)) {
      setPlanCode(repriceTerms[0]);
    }
  }, [reprice, repriceTerms, planCode]);
  const hasOffer = Boolean(offer);
  // Backend-resolved (region-aware); 7 is a transient fallback shown only while
  // the resolve is in flight (the dynamic line is hidden during loading).
  const trialDays = promoTrialDays ?? 7;
  // Today's copy, which the flag's fields replace one by one.
  const content = mergePaywallContent(
    {
      headline: t("Start your Free {{trialDays}}-Day Trial", { trialDays }),
      subhead: t(
        "Your payment will process after the {{trialDays}}-day free trial period. Cancel at any time.",
        { trialDays },
      ),
      annual_badge: "-17%",
      plan_description: t(
        "For seasoned coaches looking to grow a sustainable coaching business.",
      ),
      trial_headline: t("{{trialDays}} Days Free", { trialDays }),
      cta_text: t("Start Free Trial"),
      show_free_plan_button: true,
      free_plan_text: t("Continue with Free Plan"),
    },
    paywallFlag,
    trialDays,
  );

  const onDone = (_token?: unknown, didReprice?: boolean) => {
    if (purchase) {
      // What just happened, as the form saw it: the refreshed account is on the
      // new plan and has re-price targets of its own.
      setRepricedPlan(Boolean(didReprice));
      setShowSuccessPage(true);
    } else {
      setToastAlert(t("Your account was successfully updated."));
    }
  };

  const onPlanCodeChange = useCallback((planCode: proPlanType) => {
    setPlanCode(planCode);
  }, []);

  // Back returns into the signup funnel at the "How do you coach?" step, and
  // only exists for visitors who came from it (?from=coach-plan) — dedicated
  // marketing/invite links never saw the question, so they get no Back. The
  // rest of the query (promo, currency, sport...) rides along both ways so a
  // Back-then-reselect round trip keeps the visitor's discount context.
  const fromCoachPlan = params.get("from") === "coach-plan";
  const onBack = () => {
    const query = new URLSearchParams(location.search);
    query.delete("from");
    query.set("resume", "coach-plan");
    history.push(`/signup?${query.toString()}`);
  };

  // Short description of what the applied promo grants, shown under the badge.
  // Reads "60 days free, then 20% off for the first 12 months" — the trial
  // prefix only appears when the promo extends the trial past the standard 7
  // free days, and the discount clause only when the coupon carries one.
  const couponDescription = useMemo(() => {
    const promoDiscount = offer?.discount;
    let discount: string | null = null;
    if (promoDiscount?.percentOff) {
      if (promoDiscount.duration === "forever") {
        discount = t("{{percent}}% off for life", { percent: promoDiscount.percentOff });
      } else if (promoDiscount.duration === "repeating" && promoDiscount.durationInMonths) {
        discount = t("{{percent}}% off for the first {{months}} months", {
          percent: promoDiscount.percentOff,
          months: promoDiscount.durationInMonths,
        });
      } else {
        discount = t("{{percent}}% off your first payment", {
          percent: promoDiscount.percentOff,
        });
      }
    } else if (promoDiscount?.amountOff) {
      discount = t("{{amount}} off your first payment", {
        amount: formatPrice(promoDiscount.amountOff, currency),
      });
    }

    // A direct purchase never grants a trial, so only the discount matters.
    const showTrial = !purchase && trialDays > 7;
    if (showTrial && discount) {
      return t("{{trialDays}} days free, then {{discount}}", {
        trialDays,
        discount,
      });
    }
    if (showTrial) {
      return t("{{trialDays}} days free", { trialDays });
    }
    return discount;
  }, [offer, trialDays, currency, purchase, t]);

  // Checked before the subscription gate: once the purchase lands the user
  // HAS a subscription, and the gate would otherwise steal the success screen.
  if (showSuccessPage) {
    return (
      <Authenticated>
        <SuccessPage
          plan={planCode}
          user={self}
          reprice={repricedPlan}
          onDone={() => history.push("/")}
        />
      </Authenticated>
    );
  }

  if (sendHome) {
    return <Redirect to="/" />;
  }

  // Also held while the flag loads, so the defaults don't flash before the
  // variant.
  if ((Auth.isAuthenticated && !self) || !contentReady) {
    return (
      <div className={styles.loading}>
        <Loading isLoading loadType="spinner" />
      </div>
    );
  }

  return (
    <Authenticated redirect="/signup">
      <>
        <div
          className={cls(styles.container, "pro-trial-golf", {
            [styles.page_background]: location.pathname.includes("signup"),
          })}
        >
          <div className={styles.left_content}>
            <img
              className={styles.site_logo}
              src={coachnow_logo}
              alt="CoachNow Icon"
            />
            <div className={styles.content_container}>
              {fromCoachPlan && (
                <button
                  type="button"
                  className={styles.back}
                  onClick={onBack}
                  aria-label={t("Back")}
                >
                  <i className="fas fa-arrow-left" aria-hidden="true" />
                  {t("Back")}
                </button>
              )}
              <div className={styles.header}>
                <h3 className={styles.header_title}>
                  {reprice
                    ? t("Switch to CoachNow PRO")
                    : purchase
                    ? t("Start your CoachNow PRO subscription")
                    : loading
                    ? t("Start your Free Trial")
                    : content.headline}
                </h3>
                <p className={styles.header_description}>
                  {reprice
                    ? t("Your current plan's unused time is credited toward this payment. If the credit is larger than the payment, the rest stays on your account and covers your next bills.")
                    : purchase
                    ? t("To get started enter your card details")
                    : loading
                    ? t(
                        "Your payment will process after your free trial period. Cancel at any time.",
                      )
                    : content.subhead}
                </p>
              </div>
              {!loading && hasOffer && (
                <div className={styles.promo_applied_container}>
                  <div className={styles.promocode_applied}>
                    <img
                      src={price_tag}
                      alt="coupon tag"
                      className={styles.price_tag}
                    />
                    <div className={styles.promo_applied_wrapper}>
                      <p className={styles.promo_applied}>
                        {t("Special Offer Applied!")}
                      </p>
                      {couponDescription && (
                        <p className={styles.coupon_description}>
                          {couponDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <PricingToggle
                planCode={planCode}
                onPlanSelect={onPlanCodeChange}
                discount={hasPromo ? undefined : content.annual_badge || undefined}
                availablePlans={reprice ? repriceTerms : undefined}
              />
              {resolveError ? (
                <PromoResolveError onRetry={resolveOffer} />
              ) : (
                <PriceCard
                  planCode={planCode}
                  currency={currency}
                  loading={loading || baseAmount == null}
                  discount={offer?.discount}
                  baseAmount={baseAmount}
                  purchase={purchase}
                  description={content.plan_description}
                  trialHeadline={content.trial_headline}
                />
              )}
              {/* The plan switcher belongs to the signup funnel (the coach
                  question): dedicated marketing/purchase links sell a single
                  plan and must not cross-sell away from it. */}
              {fromCoachPlan && (
                <p className={styles.cross_link}>
                  {/* Carry the whole query across: the promo/currency context
                      and the from-marker (so Back works there too). */}
                  {/* Stay in the same mode: a direct buyer cross-links to the
                      academy purchase, a trial visitor to the academy trial. */}
                  <Link
                    to={{
                      pathname: purchase
                        ? "/purchase/academy"
                        : "/trial/academy",
                      search: location.search,
                    }}
                  >
                    {t(
                      "Need a plan that includes athlete scheduling and billing?",
                    )}{" "}
                    {t("Try CoachNow Academy")} &rarr;
                  </Link>
                </p>
              )}
              <BillingInformation
                onDone={onDone}
                planCode={planCode}
                couponCode={promo}
                currency={currency}
                purchase={purchase}
                reprice={reprice}
                ctaText={content.cta_text}
                showFreePlanButton={content.show_free_plan_button}
                freePlanText={content.free_plan_text}
              />
              <CheckoutLegalLinks />
            </div>
            <Toast success={toastAlert} onDone={() => setToastAlert(null)} />
          </div>
          <div className={styles.right_content}>
            <img
              src={onboarding}
              alt="onboarding"
              className={styles.onboarding_img}
            />
          </div>
        </div>
      </>
    </Authenticated>
  );
};

export default ProCheckout;
